import clsx from 'clsx';
import { memo, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import {
    LocaleValuesFragment,
    VssValveAmFragment,
    VssValveEuFragment,
} from 'src/generated/operations';
import { MESSAGES } from 'src/modules/common/constants';
import { AttributesList, AttributesListItem } from 'src/utils/createAttributesItem.ts';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage.tsx';
import { preventDefault } from 'src/modules/common/utils/preventDefault.ts';
import { ConfigContext } from 'src/configContext.ts';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { PDFIcon } from 'src/modules/common/icons/PDFIcon.tsx';
import { ChevronUpIcon } from 'src/modules/common/icons/ChevronUpIcon.tsx';
import { ChevronDownIcon } from 'src/modules/common/icons/ChevronDownIcon.tsx';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { AttributesAliases } from 'src/generated/dict.ts';
import { createFileGaClickEvent } from 'src/utils/createFileGaClickEvent.ts';
import { RuntimeContext } from 'src/runtimeContext';

export type DeviceProps = {
    chip?: ReactNode;
    defaultImage: ReactNode;
    isSelected?: boolean;
    product: VssValveEuFragment | VssValveAmFragment;
    attributes: [AttributesList, AttributesList];
    onDeviceClick?: (device: {label: string; id: string }) => void;
    links?: Array<LocaleValuesFragment | null | undefined>;
    price?: ReactNode;
    actions?: ReactNode;
    designData?: string;
}

export const Device = memo(({
    chip,
    defaultImage,
    isSelected,
    product,
    attributes,
    onDeviceClick,
    links = [],
    price,
    actions,
    designData = '',
}: DeviceProps) => {
    const { isMobile } = useContext(RuntimeContext);
    const { userSettings, gaPush } = useContext(ConfigContext);
    const [parametersOpen, setParametersOpen] = useState(false);

    const composedAttributes = useMemo(
        () => {
            const [firstColumn, secondColumn] = attributes;

            const firstFourAttributes = [...firstColumn, ...secondColumn].reduce((prev, curr) => {
                if (prev.length < 4) {
                    return [...prev, curr];
                }

                return prev;
            }, [] as AttributesListItem[]);

            if (isMobile && !parametersOpen) {
                return [firstFourAttributes];
            }

            return attributes;
        },
        [attributes, isMobile, parametersOpen],
    );

    const { specification } = product;
    const statical = onDeviceClick === undefined;
    const onClick = useCallback(() => {
        if (onDeviceClick) {
            onDeviceClick({
                id: product.ID,
                label: product.Label,
            });
        }
    }, [onDeviceClick, product]);

    const onFileClick = useCallback((url: string) => {
        gaPush(createFileGaClickEvent(url));
    }, [gaPush]);

    return (
        <div
            data-testid="device"
            role="button"
            tabIndex={0}
            onKeyDown={() => null}
            className={clsx({
                'vss-device': true,
                'vss-device--selected': isSelected === true,
                'vss-device--statical': statical,
            })}
            onClick={onClick}
        >
            <div className="vss-device__header">
                <div className="vss-device__details">
                    <div className="vss-device__product">
                        <ViewProductLink
                            userSettings={userSettings}
                            code={product.Label}
                            content={designData}
                        >
                            {product.Label}
                        </ViewProductLink>

                        <div className="vss-device__recommendations">
                            {chip && (chip)}
                        </div>
                    </div>
                </div>

                {!statical && (
                    <div className={clsx({
                        'vss-device__radio': true,
                        'vss-device__radio--selected': isSelected === true,
                    })}
                    />
                )}
            </div>

            <div className="vss-device__body">
                <div className="vss-device__content">
                    <div className="vss-device__image">
                        <ProductImage
                            imgSrc={product.image?.raw.join('') || ''}
                        >
                            {defaultImage}
                        </ProductImage>
                    </div>

                    <div
                        data-testid="device-parameters-wrapper"
                        className="vss-device__parameters-wrapper"
                    >
                        <div className="vss-device__parameters">
                            {composedAttributes.map((column, index) => (
                                <div
                                    className="vss-device__parameters-column"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                >
                                    {
                                        column.map(([key, value], i) => {
                                            const valueComposed = typeof value === 'string'
                                                ? value
                                                : value?.localized.join(', ');

                                            return (
                                                <div
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    key={`${key}_${i}`}
                                                    className="vss-device__parameter"
                                                    role="row"
                                                    tabIndex={0}
                                                    onKeyDown={() => null}
                                                    onClick={preventDefault}
                                                >
                                                    <div className="vss-device__label">
                                                        <I18n messageId={key}/>
                                                    </div>

                                                    <RenderHtml
                                                        className="vss-device__value"
                                                        data={valueComposed || ''}
                                                    />
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {price && (
                    <div className="vss-device__price">
                        <div className="vss-device__price-label">
                            <I18n messageId={MESSAGES.DevicePricePerUnit}/>
                        </div>

                        <div className="vss-device__price-value">
                            {price}
                        </div>
                    </div>
                )}

                <div className="vss-device__footer">
                    <div className="vss-device__links">
                        {specification && (
                            <a
                                href={specification.localized.join('')}
                                onClick={() => onFileClick(specification?.localized.join(''))}
                                className="vss-device__link"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <I18n messageId={AttributesAliases.specification}/>

                                <span className="vss-device__link-icon">
                                    <PDFIcon/>
                                </span>
                            </a>
                        )}
                        {links && links
                            .map((link) => {
                                if (!link) {
                                    return '';
                                }
                                return (
                                    <a
                                        key={link.localized.join('')}
                                        href={link.localized.join('')}
                                        onClick={() => onFileClick(link?.localized.join(''))}
                                        className="vss-device__link"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <I18n messageId={AttributesAliases.specification}/>

                                        <span className="vss-device__link-icon">
                                            <PDFIcon/>
                                        </span>
                                    </a>
                                );
                            })}
                    </div>

                    <div className="vss-device__actions">
                        {actions}

                        {isMobile ? (
                            <div
                                data-testid="device-parameters-toggle"
                                role="button"
                                tabIndex={0}
                                onKeyDown={() => null}
                                className="vss-device__parameters-toggle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setParametersOpen((prev) => !prev);
                                }}
                            >
                                <div className="vss-device__parameters-toggle-text">
                                    <I18n messageId={parametersOpen ? MESSAGES.hide : MESSAGES.VIEW_MORE}/>
                                </div>

                                <div className="vss-device__parameters-toggle-icon">
                                    {parametersOpen ? <ChevronUpIcon/> : <ChevronDownIcon/>}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
});
